import React, { useEffect, useState } from 'react';
import delve from 'dlv';
import { compose } from 'recompose';
import { withAnalytic } from '@skooldio/analytics-provider';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import urlJoin from 'url-join';

import NetworkStatusProvider from 'Context/NetworkStatusProvider';
import InternetConnection from 'Containers/InternetConnection';
import useGetDeviceInfo from 'Util/Hooks/useGetDeviceInfo';

import { genUserEvent, USER_ACTIONS } from '../../../Util/Analytics';
import { withConfig } from '../../../Context/ConfigContext';
import ANYLoginView from '../../../Components/Login';
import AppIncompletedContent from '../../../Components/AppIncompletedContent';

const MODE = {
  ANY: 'ANY',
  PAAS: 'PAAS',
  SKD_APP: 'SKD_APP',
};

const LoginView = ({
  appConfig,
  login,
  isLoading,
  clearLoginError,
  onLogAction,
  history,
  capacitorContext,
  loginError,
  t,
}) => {
  useEffect(() => {
    clearLoginError();
  }, []);

  const { mode, registerURL } = delve(appConfig, 'skooldioLogin');
  const appVersionConfig = delve(appConfig, 'appVersion');

  const { appVersion: appVersionInfo } = useGetDeviceInfo();

  // FIXME: For workaround appVersion from Capacitor when contextIsolation is true
  const [electronAppVersion, setElectronAppVersion] = useState(null);
  const isElectron = !!window.electron?.isElectron;

  useEffect(() => {
    let isMounted = true;

    if (isElectron) {
      window.electron.getAppVersion().then((version) => {
        if (isMounted) {
          setElectronAppVersion(version);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [isElectron]);

  const currentVersion = isElectron ? electronAppVersion : appVersionInfo;
  const versionNo = currentVersion || appVersionConfig;

  const handleLogInClick = (values) => {
    // 'values' consists of userName = string, password = string, rememberData = boolean.
    const loginData = {
      email: values.email,
      // username: values.userName,
      password: values.password,
    };

    console.log('loginData:', loginData);
    login(loginData);
  };

  const handleRegisterClick = () => {
    if (registerURL) {
      onLogAction(genUserEvent(USER_ACTIONS.REGISTER, 'Login Page'));
      setTimeout(() => {
        window.location = registerURL;
      }, 10);
    } else {
      onLogAction(genUserEvent(USER_ACTIONS.REGISTER, 'Login Page'));
      history.push('/register');
    }
  };

  const handlePassportLoginClick = (loginURL, provider) => {
    onLogAction(genUserEvent(USER_ACTIONS.LOGIN, 'Login Page'));
    setTimeout(async () => {
      const { openUrl, setUserProvider } = capacitorContext;
      setUserProvider(provider);

      const locationId =
        (await window.electron?.getApplicationLocationId?.()) ||
        window?.process?.env?.SKOOLDIO_APPLICATION_LOCATION_ID;

      /**
       * If specified `locationId` will be attached to passport authorize url via query param
       */
      const loginWithLocationURL = locationId
        ? urlJoin(loginURL, `?loc_id=${locationId}`)
        : loginURL;

      openUrl(loginWithLocationURL, () => {
        window.location = loginWithLocationURL;
      });
    }, 10);
  };

  switch (mode) {
    case MODE.SKD_APP:
      return <AppIncompletedContent />;
    case MODE.ANY:
    default:
      return (
        <NetworkStatusProvider>
          <InternetConnection />
          <ANYLoginView
            onPassportLoginClick={handlePassportLoginClick}
            note={t('COPYRIGHT', { appVersion: versionNo, year: new Date().getFullYear() })}
          />
        </NetworkStatusProvider>
      );
  }
};

LoginView.propTypes = {
  login: PropTypes.func,
  clearLoginError: PropTypes.func,
  onLogAction: PropTypes.func,
  history: PropTypes.object,
  capacitorContext: PropTypes.object,
  loginError: PropTypes.oneOfType([
    PropTypes.shape({
      details: PropTypes.arrayOf(
        PropTypes.shape({
          message: PropTypes.string,
        })
      ),
    }),
    PropTypes.shape({
      rawObject: PropTypes.shape({
        error: PropTypes.shape({
          message: PropTypes.string,
        }),
      }),
    }),
  ]),
};

export default compose(withConfig, withAnalytic, withTranslation('registerLogin'))(LoginView);
